import { z } from "zod";
import { bookProductionProductSchema, bookProductionVersionBindingV1Schema, } from "./book-production-evidence.js";
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/u);
const opaqueIdSchema = z.string().trim().min(1).max(240);
const timestampSchema = z.string().datetime({ offset: true });
const safeCodeSchema = z
    .string()
    .trim()
    .min(1)
    .max(160)
    .regex(/^[A-Z0-9][A-Z0-9_.]*$/u);
function requireUnique(values, context, path, label) {
    if (new Set(values).size !== values.length)
        context.addIssue({ code: "custom", path, message: `${label} must be unique.` });
}
export const bookPublishingAssetRegistryContractSchema = z.literal("BOOK_PUBLISHING_ASSET_REGISTRY_V1");
export const bookPublishingAssetUseContractSchema = z.literal("BOOK_PUBLISHING_ASSET_USE_V1");
export const bookPublishingAssetClassSchema = z.enum([
    "NEUTRAL_BRAND",
    "PUBLISHING_PRIMITIVE",
    "PRODUCT_DIRECTION_ART",
    "CUSTOMER_SOURCE_MEDIA",
    "CUSTOMER_DERIVATIVE",
    "BOOK_GENERATED_ART",
    "FICTIONAL_DEMO_FIXTURE",
    "REFERENCE_ONLY",
]);
export const bookPublishingAssetReuseScopeSchema = z.enum([
    "CROSS_PRODUCT",
    "PRODUCT_ONLY",
    "DIRECTION_ONLY",
    "BOOK_REVISION_ONLY",
    "DEMO_ONLY",
    "REFERENCE_ONLY",
]);
export const bookPublishingAssetPurposeSchema = z.enum([
    "DIRECTION_KIT_COMPONENT",
    "CUSTOMER_BOOK_RENDITION",
    "PUBLIC_DEMO",
    "INTERNAL_REVIEW",
    "REGRESSION_VALIDATION",
]);
export const bookPublishingAssetChannelSchema = z.enum([
    "PRINT",
    "DIGITAL_FIXED",
    "DIGITAL_REFLOW",
    "WEB_APPLICATION",
    "MARKETING",
    "INTERNAL_ONLY",
]);
export const bookPublishingAssetRenderDispositionSchema = z.enum([
    "DIRECT_RENDER",
    "DERIVATIVE_INPUT",
    "REBUILD_AS_SEMANTIC_COMPONENT",
    "REFERENCE_VALIDATION_ONLY",
]);
export const bookPublishingAssetLineageBindingV1Schema = z
    .object({
    registryId: opaqueIdSchema,
    registryRevision: z.number().int().positive(),
    registrySourceHashSha256: sha256Schema,
    assetId: opaqueIdSchema,
    assetRecordDigestSha256: sha256Schema,
    objectVersionStatus: z.enum(["SUPPLIED", "NOT_SUPPLIED"]),
    objectVersionId: z.string().trim().min(1).max(500).nullable(),
    checksumSha256: sha256Schema,
})
    .strict()
    .superRefine((value, context) => {
    if ((value.objectVersionStatus === "SUPPLIED") !== (value.objectVersionId !== null))
        context.addIssue({
            code: "custom",
            path: ["objectVersionId"],
            message: "Lineage bindings must preserve an explicitly supplied or missing object version.",
        });
});
export const bookPublishingAssetExactBindingV1Schema = bookPublishingAssetLineageBindingV1Schema.safeExtend({
    registryDigestSha256: sha256Schema,
    checkpointManifestChecksumSha256: sha256Schema,
});
export const bookPublishingAssetObjectV1Schema = z
    .object({
    storageClass: z.enum([
        "SEALED_LOCAL_VAULT",
        "PRIVATE_IMMUTABLE_OBJECT_STORE",
        "CUSTOMER_MEDIA_STORE",
    ]),
    objectKeyDigestSha256: sha256Schema,
    objectVersionStatus: z.enum(["SUPPLIED", "NOT_SUPPLIED"]),
    objectVersionId: z.string().trim().min(1).max(500).nullable(),
    checksumSha256: sha256Schema,
    byteSize: z.number().int().positive().max(2_000_000_000),
    contentType: z.string().trim().min(1).max(200),
    pixelDimensions: z
        .object({
        width: z.number().int().positive(),
        height: z.number().int().positive(),
    })
        .strict()
        .optional(),
    effectivePpi: z.number().positive().max(10_000).optional(),
    colorSpace: z.enum(["SRGB", "ADOBE_RGB", "DISPLAY_P3", "CMYK", "GRAY", "UNKNOWN"]),
    binaryStoragePolicy: z.literal("EXTERNAL_IMMUTABLE_ONLY"),
})
    .strict()
    .superRefine((value, context) => {
    if ((value.objectVersionStatus === "SUPPLIED") !== (value.objectVersionId !== null))
        context.addIssue({
            code: "custom",
            path: ["objectVersionId"],
            message: "A supplied object version requires its exact ID; an unavailable version must remain explicitly null.",
        });
});
export const bookPublishingAssetRightsV1Schema = z
    .object({
    basis: z.enum([
        "OWNED",
        "LICENSED",
        "PUBLIC_DOMAIN",
        "CUSTOMER_ATTESTED",
        "COMPOSITE",
        "FIXTURE_ONLY",
        "NOT_ESTABLISHED",
    ]),
    suppliedStatus: z.enum([
        "SUPPLIED_ACTIVE",
        "SUPPLIED_RESTRICTED",
        "NOT_SUPPLIED",
        "SUPPLIED_WITHDRAWN",
    ]),
    evidenceDigestSha256: sha256Schema.optional(),
    permittedPurposes: z.array(bookPublishingAssetPurposeSchema).max(5),
    permittedChannels: z.array(bookPublishingAssetChannelSchema).max(6),
    validThrough: timestampSchema.optional(),
})
    .strict()
    .superRefine((value, context) => {
    requireUnique(value.permittedPurposes, context, ["permittedPurposes"], "Rights purposes");
    requireUnique(value.permittedChannels, context, ["permittedChannels"], "Rights channels");
    if (value.suppliedStatus.startsWith("SUPPLIED_") && !value.evidenceDigestSha256)
        context.addIssue({
            code: "custom",
            path: ["evidenceDigestSha256"],
            message: "A supplied rights status requires an exact evidence digest.",
        });
    if (["SUPPLIED_ACTIVE", "SUPPLIED_RESTRICTED"].includes(value.suppliedStatus) &&
        (value.permittedPurposes.length === 0 || value.permittedChannels.length === 0))
        context.addIssue({
            code: "custom",
            path: ["permittedPurposes"],
            message: "Supplied usable rights require at least one exact purpose and channel.",
        });
    if (value.basis === "NOT_ESTABLISHED" && value.suppliedStatus !== "NOT_SUPPLIED")
        context.addIssue({
            code: "custom",
            path: ["suppliedStatus"],
            message: "Rights that are not established cannot be supplied as active or restricted.",
        });
    if (value.basis === "FIXTURE_ONLY" &&
        value.permittedPurposes.some((purpose) => purpose === "CUSTOMER_BOOK_RENDITION"))
        context.addIssue({
            code: "custom",
            path: ["permittedPurposes"],
            message: "Fixture-only rights cannot permit customer-book rendition use.",
        });
    if (["NOT_SUPPLIED", "SUPPLIED_WITHDRAWN"].includes(value.suppliedStatus) &&
        (value.permittedPurposes.length > 0 || value.permittedChannels.length > 0))
        context.addIssue({
            code: "custom",
            path: ["permittedPurposes"],
            message: "Absent or withdrawn rights cannot declare permitted uses.",
        });
});
export const bookPublishingAssetConsentV1Schema = z
    .object({
    applicability: z.enum(["NOT_APPLICABLE", "REQUIRED"]),
    suppliedStatus: z.enum([
        "NOT_APPLICABLE",
        "SUPPLIED_GRANTED",
        "SUPPLIED_WITHHELD",
        "NOT_SUPPLIED",
    ]),
    subjectEvidence: z
        .array(z
        .object({
        authoritySubjectId: opaqueIdSchema,
        evidenceDigestSha256: sha256Schema,
    })
        .strict())
        .max(100),
})
    .strict()
    .superRefine((value, context) => {
    requireUnique(value.subjectEvidence.map(({ authoritySubjectId }) => authoritySubjectId), context, ["subjectEvidence"], "Consent authority subject IDs");
    if (value.applicability === "NOT_APPLICABLE" &&
        (value.suppliedStatus !== "NOT_APPLICABLE" || value.subjectEvidence.length > 0))
        context.addIssue({
            code: "custom",
            path: ["suppliedStatus"],
            message: "Non-applicable consent cannot carry subjects or supplied consent evidence.",
        });
    if (value.applicability === "REQUIRED" && value.suppliedStatus === "NOT_APPLICABLE")
        context.addIssue({
            code: "custom",
            path: ["suppliedStatus"],
            message: "Required consent cannot be marked not applicable.",
        });
    if (["SUPPLIED_GRANTED", "SUPPLIED_WITHHELD"].includes(value.suppliedStatus) &&
        value.subjectEvidence.length === 0)
        context.addIssue({
            code: "custom",
            path: ["subjectEvidence"],
            message: "A supplied granted or withheld decision requires exact subjects and evidence digests.",
        });
    if (value.suppliedStatus === "NOT_SUPPLIED" && value.subjectEvidence.length > 0)
        context.addIssue({
            code: "custom",
            path: ["subjectEvidence"],
            message: "Consent that was not supplied cannot carry ambiguous decision evidence.",
        });
});
export const bookPublishingAssetProvenanceV1Schema = z
    .object({
    origin: z.enum([
        "LC93_CANONICAL_PACKAGE",
        "PRODUCT_AUTHORED",
        "CUSTOMER_UPLOAD",
        "DETERMINISTIC_DERIVATION",
        "PROVIDER_GENERATED",
        "PUBLIC_DOMAIN_SOURCE",
    ]),
    evidenceDigestSha256: sha256Schema,
    authorityTier: z.string().trim().min(1).max(160),
    registryUsageRuleDigestSha256: sha256Schema,
})
    .strict();
const parentBindingsSchema = z
    .array(bookPublishingAssetLineageBindingV1Schema)
    .min(1)
    .max(100)
    .superRefine((values, context) => requireUnique(values.map(({ registryId, assetId }) => `${registryId}:${assetId}`), context, [], "Lineage parent bindings"));
const originalLineageSchema = z
    .object({
    kind: z.literal("ORIGINAL"),
    originSourceDigestSha256: sha256Schema,
})
    .strict();
const deterministicLineageSchema = z
    .object({
    kind: z.literal("DETERMINISTIC_DERIVATIVE"),
    parentBindings: parentBindingsSchema,
    operation: z.enum([
        "EXACT_CROP",
        "SOURCE_PIXEL_ALPHA_EXTRACTION",
        "RESPONSIVE_ENCODING",
        "COLOR_CONVERSION",
        "DETERMINISTIC_LAYER_COMPOSITION",
        "SEMANTIC_REBUILD_REFERENCE",
    ]),
    transformBinding: bookProductionVersionBindingV1Schema,
    compositionSpecBinding: bookProductionVersionBindingV1Schema.optional(),
    compositionLayers: z
        .array(z
        .object({
        parentAssetId: opaqueIdSchema,
        parentAssetRecordDigestSha256: sha256Schema,
        role: safeCodeSchema,
        zIndex: z.number().int().min(-10_000).max(10_000),
    })
        .strict())
        .max(100)
        .optional(),
    inheritedLimitationDigestsSha256: z.array(sha256Schema).max(100),
    inheritedRightsEvidenceDigestsSha256: z.array(sha256Schema).max(100),
    inheritedConsentEvidenceDigestsSha256: z.array(sha256Schema).max(100),
    restrictionsInherited: z.literal(true),
})
    .strict()
    .superRefine((value, context) => {
    if (value.operation === "DETERMINISTIC_LAYER_COMPOSITION" && value.parentBindings.length < 2)
        context.addIssue({
            code: "custom",
            path: ["parentBindings"],
            message: "A deterministic layer composition requires at least two exact parents.",
        });
    if (value.operation === "DETERMINISTIC_LAYER_COMPOSITION") {
        if (!value.compositionSpecBinding || !value.compositionLayers)
            context.addIssue({
                code: "custom",
                path: ["compositionLayers"],
                message: "A deterministic layer composition requires an exact composition spec and ordered role-bound layers.",
            });
        if (value.compositionLayers) {
            requireUnique(value.compositionLayers.map(({ parentAssetId }) => parentAssetId), context, ["compositionLayers"], "Composition-layer parent asset IDs");
            requireUnique(value.compositionLayers.map(({ zIndex }) => String(zIndex)), context, ["compositionLayers"], "Composition-layer z-indices");
            const parentsById = new Map(value.parentBindings.map((binding) => [binding.assetId, binding]));
            if (value.compositionLayers.length !== value.parentBindings.length ||
                value.compositionLayers.some((layer) => parentsById.get(layer.parentAssetId)?.assetRecordDigestSha256 !==
                    layer.parentAssetRecordDigestSha256))
                context.addIssue({
                    code: "custom",
                    path: ["compositionLayers"],
                    message: "Composition layers must bind every exact parent asset and record digest exactly once.",
                });
        }
    }
    if (value.operation !== "DETERMINISTIC_LAYER_COMPOSITION" && value.parentBindings.length !== 1)
        context.addIssue({
            code: "custom",
            path: ["parentBindings"],
            message: "A non-composite deterministic transform requires exactly one exact parent.",
        });
    if (value.operation !== "DETERMINISTIC_LAYER_COMPOSITION" &&
        (value.compositionSpecBinding || value.compositionLayers))
        context.addIssue({
            code: "custom",
            path: ["compositionLayers"],
            message: "Only a deterministic layer composition may carry layer-order semantics.",
        });
    requireUnique(value.inheritedLimitationDigestsSha256, context, ["inheritedLimitationDigestsSha256"], "Inherited limitation digests");
    requireUnique(value.inheritedRightsEvidenceDigestsSha256, context, ["inheritedRightsEvidenceDigestsSha256"], "Inherited rights evidence digests");
    requireUnique(value.inheritedConsentEvidenceDigestsSha256, context, ["inheritedConsentEvidenceDigestsSha256"], "Inherited consent evidence digests");
});
const restorationLineageSchema = z
    .object({
    kind: z.literal("RESTORATION"),
    parentBindings: parentBindingsSchema,
    transformBinding: bookProductionVersionBindingV1Schema,
    restorationPolicyBinding: bookProductionVersionBindingV1Schema,
    inheritedLimitationDigestsSha256: z.array(sha256Schema).max(100),
    inheritedRightsEvidenceDigestsSha256: z.array(sha256Schema).max(100),
    inheritedConsentEvidenceDigestsSha256: z.array(sha256Schema).max(100),
    restrictionsInherited: z.literal(true),
})
    .strict()
    .superRefine((value, context) => {
    requireUnique(value.inheritedLimitationDigestsSha256, context, ["inheritedLimitationDigestsSha256"], "Inherited limitation digests");
    requireUnique(value.inheritedRightsEvidenceDigestsSha256, context, ["inheritedRightsEvidenceDigestsSha256"], "Inherited rights evidence digests");
    requireUnique(value.inheritedConsentEvidenceDigestsSha256, context, ["inheritedConsentEvidenceDigestsSha256"], "Inherited consent evidence digests");
});
const generatedLineageSchema = z
    .object({
    kind: z.literal("GENERATED"),
    referenceBindings: z.array(bookPublishingAssetLineageBindingV1Schema).max(100),
    executionId: opaqueIdSchema,
    promptBindingId: opaqueIdSchema,
    modelBinding: bookProductionVersionBindingV1Schema,
    providerCallId: opaqueIdSchema,
    providerOutputRightsBinding: bookProductionVersionBindingV1Schema,
    providerOutputRightsEvidenceDigestSha256: sha256Schema,
    inheritedLimitationDigestsSha256: z.array(sha256Schema).max(100),
    inheritedRightsEvidenceDigestsSha256: z.array(sha256Schema).max(100),
    inheritedConsentEvidenceDigestsSha256: z.array(sha256Schema).max(100),
    restrictionsInherited: z.literal(true),
})
    .strict()
    .superRefine((value, context) => {
    requireUnique(value.referenceBindings.map(({ registryId, assetId }) => `${registryId}:${assetId}`), context, ["referenceBindings"], "Generation reference bindings");
    requireUnique(value.inheritedLimitationDigestsSha256, context, ["inheritedLimitationDigestsSha256"], "Inherited limitation digests");
    requireUnique(value.inheritedRightsEvidenceDigestsSha256, context, ["inheritedRightsEvidenceDigestsSha256"], "Inherited rights evidence digests");
    requireUnique(value.inheritedConsentEvidenceDigestsSha256, context, ["inheritedConsentEvidenceDigestsSha256"], "Inherited consent evidence digests");
});
export const bookPublishingAssetLineageV1Schema = z.discriminatedUnion("kind", [
    originalLineageSchema,
    deterministicLineageSchema,
    restorationLineageSchema,
    generatedLineageSchema,
]);
const bookPublishingAssetBodyV1Schema = z
    .object({
    assetId: opaqueIdSchema,
    registryAssetId: opaqueIdSchema,
    registryEntryDigestSha256: sha256Schema,
    assetRevision: z.number().int().positive(),
    immutable: z.literal(true),
    assetClass: bookPublishingAssetClassSchema,
    reuseScope: bookPublishingAssetReuseScopeSchema,
    allowedProducts: z.array(bookProductionProductSchema).max(5),
    allowedDirectionIds: z.array(opaqueIdSchema).max(100),
    bookBinding: z
        .object({
        product: bookProductionProductSchema,
        bookId: opaqueIdSchema,
        revisionId: opaqueIdSchema,
    })
        .strict()
        .optional(),
    fictionalContent: z.boolean(),
    containsIdentifiablePeople: z.boolean(),
    renderDisposition: bookPublishingAssetRenderDispositionSchema,
    object: bookPublishingAssetObjectV1Schema,
    rights: bookPublishingAssetRightsV1Schema,
    consent: bookPublishingAssetConsentV1Schema,
    provenance: bookPublishingAssetProvenanceV1Schema,
    lineage: bookPublishingAssetLineageV1Schema,
    sourceLimitationDigestsSha256: z.array(sha256Schema).max(100),
    truthBoundary: z.literal("SUPPLIED_ASSET_METADATA_NOT_AUTHENTICATED"),
})
    .strict()
    .superRefine((value, context) => {
    requireUnique(value.allowedProducts, context, ["allowedProducts"], "Allowed products");
    requireUnique(value.allowedDirectionIds, context, ["allowedDirectionIds"], "Allowed direction IDs");
    requireUnique(value.sourceLimitationDigestsSha256, context, ["sourceLimitationDigestsSha256"], "Source limitation digests");
    if (value.reuseScope === "CROSS_PRODUCT") {
        if (!["NEUTRAL_BRAND", "PUBLISHING_PRIMITIVE"].includes(value.assetClass))
            context.addIssue({
                code: "custom",
                path: ["assetClass"],
                message: "Only neutral brand or publishing primitives may be cross-product assets.",
            });
        if (value.allowedProducts.length < 2)
            context.addIssue({
                code: "custom",
                path: ["allowedProducts"],
                message: "A cross-product asset must name at least two products.",
            });
        if (value.allowedDirectionIds.length > 0 || value.bookBinding)
            context.addIssue({
                code: "custom",
                path: ["reuseScope"],
                message: "Cross-product assets cannot carry a direction or book binding.",
            });
    }
    if (value.reuseScope === "PRODUCT_ONLY") {
        if (value.allowedProducts.length !== 1 ||
            value.allowedDirectionIds.length > 0 ||
            value.bookBinding)
            context.addIssue({
                code: "custom",
                path: ["reuseScope"],
                message: "Product-only assets require exactly one product and no direction or book binding.",
            });
    }
    if (value.reuseScope === "DIRECTION_ONLY") {
        if (value.allowedProducts.length !== 1 ||
            value.allowedDirectionIds.length === 0 ||
            value.bookBinding)
            context.addIssue({
                code: "custom",
                path: ["reuseScope"],
                message: "Direction-only assets require one product, at least one direction, and no book binding.",
            });
    }
    if (["DEMO_ONLY", "REFERENCE_ONLY"].includes(value.reuseScope) &&
        (value.allowedDirectionIds.length > 0 || value.bookBinding))
        context.addIssue({
            code: "custom",
            path: ["reuseScope"],
            message: "Demo and reference assets cannot carry direction or customer-book bindings.",
        });
    if (value.reuseScope === "BOOK_REVISION_ONLY") {
        if (value.allowedProducts.length !== 1 ||
            !value.bookBinding ||
            value.bookBinding.product !== value.allowedProducts[0])
            context.addIssue({
                code: "custom",
                path: ["bookBinding"],
                message: "Book-revision assets require one matching product and an exact book/revision binding.",
            });
    }
    if (value.assetClass === "PRODUCT_DIRECTION_ART" && value.reuseScope !== "DIRECTION_ONLY")
        context.addIssue({
            code: "custom",
            path: ["reuseScope"],
            message: "Product direction art must remain scoped to exact directions.",
        });
    if (["CUSTOMER_SOURCE_MEDIA", "CUSTOMER_DERIVATIVE", "BOOK_GENERATED_ART"].includes(value.assetClass) &&
        value.reuseScope !== "BOOK_REVISION_ONLY")
        context.addIssue({
            code: "custom",
            path: ["reuseScope"],
            message: "Customer and generated book assets cannot escape their exact book revision.",
        });
    if ((value.assetClass === "FICTIONAL_DEMO_FIXTURE") !==
        (value.reuseScope === "DEMO_ONLY" && value.fictionalContent))
        context.addIssue({
            code: "custom",
            path: ["fictionalContent"],
            message: "Fictional fixtures must be fictional and demo-only; other asset classes cannot use that scope.",
        });
    if (value.assetClass !== "FICTIONAL_DEMO_FIXTURE" && value.fictionalContent)
        context.addIssue({
            code: "custom",
            path: ["fictionalContent"],
            message: "Fictional content must remain explicitly classified as a fictional demo fixture.",
        });
    if ((value.assetClass === "REFERENCE_ONLY") !== (value.reuseScope === "REFERENCE_ONLY"))
        context.addIssue({
            code: "custom",
            path: ["reuseScope"],
            message: "Reference-only classification and scope must agree.",
        });
    if (value.reuseScope === "REFERENCE_ONLY" &&
        !["REFERENCE_VALIDATION_ONLY", "REBUILD_AS_SEMANTIC_COMPONENT"].includes(value.renderDisposition))
        context.addIssue({
            code: "custom",
            path: ["renderDisposition"],
            message: "Reference assets cannot be directly rendered.",
        });
    if (value.containsIdentifiablePeople && value.consent.applicability !== "REQUIRED")
        context.addIssue({
            code: "custom",
            path: ["consent", "applicability"],
            message: "Assets containing identifiable people require explicit consent semantics.",
        });
    if (value.assetClass === "BOOK_GENERATED_ART") {
        const originalProviderOutput = value.lineage.kind === "GENERATED" && value.provenance.origin === "PROVIDER_GENERATED";
        const governedDerivative = ["DETERMINISTIC_DERIVATIVE", "RESTORATION"].includes(value.lineage.kind) &&
            value.provenance.origin === "DETERMINISTIC_DERIVATION";
        if (!originalProviderOutput && !governedDerivative)
            context.addIssue({
                code: "custom",
                path: ["lineage"],
                message: "Generated book art requires either exact provider-generation lineage or a governed deterministic derivative of generated art.",
            });
        if (value.lineage.kind === "GENERATED" &&
            value.lineage.referenceBindings.length > 0 &&
            value.rights.basis !== "COMPOSITE")
            context.addIssue({
                code: "custom",
                path: ["rights", "basis"],
                message: "Generated art with governed references requires composite rights in addition to exact provider-output terms evidence.",
            });
        if (value.lineage.kind === "GENERATED" &&
            value.lineage.referenceBindings.length === 0 &&
            !["OWNED", "LICENSED"].includes(value.rights.basis))
            context.addIssue({
                code: "custom",
                path: ["rights", "basis"],
                message: "Unreferenced provider output must bind supplied owned or licensed output rights; customer/source rights are insufficient.",
            });
    }
    else if (value.lineage.kind === "GENERATED")
        context.addIssue({
            code: "custom",
            path: ["assetClass"],
            message: "Generated lineage must remain classified as book-generated art.",
        });
    if (value.rights.basis === "COMPOSITE" && value.lineage.kind === "ORIGINAL")
        context.addIssue({
            code: "custom",
            path: ["rights", "basis"],
            message: "Composite rights require exact parent lineage; an original cannot self-claim it.",
        });
    if (value.assetClass === "CUSTOMER_DERIVATIVE" &&
        !["DETERMINISTIC_DERIVATIVE", "RESTORATION"].includes(value.lineage.kind))
        context.addIssue({
            code: "custom",
            path: ["lineage"],
            message: "Customer derivatives require exact deterministic or restoration lineage.",
        });
    const originAllowedClasses = {
        LC93_CANONICAL_PACKAGE: [
            "NEUTRAL_BRAND",
            "PUBLISHING_PRIMITIVE",
            "PRODUCT_DIRECTION_ART",
            "FICTIONAL_DEMO_FIXTURE",
            "REFERENCE_ONLY",
        ],
        PRODUCT_AUTHORED: ["NEUTRAL_BRAND", "PUBLISHING_PRIMITIVE", "PRODUCT_DIRECTION_ART"],
        CUSTOMER_UPLOAD: ["CUSTOMER_SOURCE_MEDIA"],
        DETERMINISTIC_DERIVATION: [
            "NEUTRAL_BRAND",
            "PUBLISHING_PRIMITIVE",
            "PRODUCT_DIRECTION_ART",
            "CUSTOMER_DERIVATIVE",
            "BOOK_GENERATED_ART",
            "FICTIONAL_DEMO_FIXTURE",
            "REFERENCE_ONLY",
        ],
        PROVIDER_GENERATED: ["BOOK_GENERATED_ART"],
        PUBLIC_DOMAIN_SOURCE: ["NEUTRAL_BRAND", "PUBLISHING_PRIMITIVE", "PRODUCT_DIRECTION_ART"],
    };
    if (!originAllowedClasses[value.provenance.origin].includes(value.assetClass))
        context.addIssue({
            code: "custom",
            path: ["provenance", "origin"],
            message: `${value.provenance.origin} provenance is incompatible with ${value.assetClass}.`,
        });
    if (value.provenance.origin === "DETERMINISTIC_DERIVATION" &&
        !["DETERMINISTIC_DERIVATIVE", "RESTORATION"].includes(value.lineage.kind))
        context.addIssue({
            code: "custom",
            path: ["lineage"],
            message: "Deterministic-derivation provenance requires derivative lineage.",
        });
    const parentBindings = value.lineage.kind === "GENERATED"
        ? value.lineage.referenceBindings
        : value.lineage.kind === "ORIGINAL"
            ? []
            : value.lineage.parentBindings;
    if (parentBindings.some(({ assetId }) => assetId === value.assetId))
        context.addIssue({
            code: "custom",
            path: ["lineage"],
            message: "An asset cannot name itself as a lineage parent.",
        });
});
export const bookPublishingAssetV1Schema = bookPublishingAssetBodyV1Schema.safeExtend({
    assetRecordDigestSha256: sha256Schema,
});
const privateBackupEvidenceSchema = z.discriminatedUnion("status", [
    z
        .object({
        status: z.literal("NOT_SUPPLIED"),
        reasonCode: safeCodeSchema,
    })
        .strict(),
    z
        .object({
        status: z.literal("SUPPLIED_VERIFICATION_RECORD"),
        evidenceDigestSha256: sha256Schema,
        manifestChecksumSha256: sha256Schema,
        objectCount: z.number().int().positive(),
        verifiedAt: timestampSchema,
    })
        .strict(),
]);
const bookPublishingAssetRegistryBodyV1Schema = z
    .object({
    contract: bookPublishingAssetRegistryContractSchema,
    registryId: opaqueIdSchema,
    registryRevision: z.number().int().positive(),
    immutable: z.literal(true),
    registrySourceBinding: bookProductionVersionBindingV1Schema,
    policyBinding: bookProductionVersionBindingV1Schema,
    recordedAt: timestampSchema,
    checkpoint: z
        .object({
        checkpointId: opaqueIdSchema,
        manifestChecksumSha256: sha256Schema,
        nonOverwriting: z.literal(true),
        totalFileCount: z.number().int().positive(),
        manifestEntryCount: z.number().int().positive(),
    })
        .strict()
        .refine(({ manifestEntryCount, totalFileCount }) => manifestEntryCount <= totalFileCount, "A checksum manifest cannot describe more entries than the sealed checkpoint contains."),
    privateBackupEvidence: privateBackupEvidenceSchema,
    binaryStoragePolicy: z.literal("EXTERNAL_IMMUTABLE_ONLY"),
    assets: z.array(bookPublishingAssetV1Schema).min(1).max(20_000),
    truthBoundary: z.literal("SUPPLIED_ASSET_METADATA_NOT_AUTHENTICATED"),
})
    .strict()
    .superRefine((value, context) => {
    requireUnique(value.assets.map(({ assetId }) => assetId), context, ["assets"], "Publishing asset IDs");
    requireUnique(value.assets.map(({ object }) => `${object.storageClass}:${object.objectKeyDigestSha256}:${object.objectVersionStatus}:${object.objectVersionId ?? "NOT_SUPPLIED"}`), context, ["assets"], "Publishing asset object versions");
    if (value.privateBackupEvidence.status === "SUPPLIED_VERIFICATION_RECORD" &&
        (value.privateBackupEvidence.manifestChecksumSha256 !==
            value.checkpoint.manifestChecksumSha256 ||
            value.privateBackupEvidence.objectCount !== value.checkpoint.totalFileCount))
        context.addIssue({
            code: "custom",
            path: ["privateBackupEvidence"],
            message: "Supplied private-backup evidence must bind the exact checkpoint manifest and total file count.",
        });
    if (value.privateBackupEvidence.status === "SUPPLIED_VERIFICATION_RECORD" &&
        Date.parse(value.privateBackupEvidence.verifiedAt) < Date.parse(value.recordedAt))
        context.addIssue({
            code: "custom",
            path: ["privateBackupEvidence", "verifiedAt"],
            message: "Private-backup verification cannot precede the registry snapshot it verifies.",
        });
});
export const bookPublishingAssetRegistryV1Schema = bookPublishingAssetRegistryBodyV1Schema.safeExtend({ registryDigestSha256: sha256Schema });
const bookPublishingAssetUseBodyV1Schema = z
    .object({
    contract: bookPublishingAssetUseContractSchema,
    useId: opaqueIdSchema,
    immutable: z.literal(true),
    assetBinding: bookPublishingAssetExactBindingV1Schema,
    product: bookProductionProductSchema,
    directionId: opaqueIdSchema.optional(),
    kitId: opaqueIdSchema.optional(),
    bookId: opaqueIdSchema.optional(),
    revisionId: opaqueIdSchema.optional(),
    productPageRole: safeCodeSchema.optional(),
    componentId: opaqueIdSchema.optional(),
    purpose: bookPublishingAssetPurposeSchema,
    channel: bookPublishingAssetChannelSchema,
    renderMode: z.enum([
        "DIRECT_RENDER",
        "DERIVATIVE_INPUT",
        "SEMANTIC_COMPONENT_REFERENCE",
        "REFERENCE_VALIDATION",
    ]),
    meaning: z.enum(["DECORATIVE", "MEANINGFUL", "COMPONENT_APPEARANCE_TARGET"]),
    contextualAltDigestSha256: sha256Schema.optional(),
    contextualAltSourceClass: z
        .enum(["BOOK_CONTEXT_AUTHORED", "CUSTOMER_APPROVED", "EDITORIAL_SEMANTIC_DERIVATION"])
        .optional(),
    contextualAltSourceBindingDigestSha256: sha256Schema.optional(),
    contextualAltBookBinding: z
        .object({ bookId: opaqueIdSchema, revisionId: opaqueIdSchema })
        .strict()
        .optional(),
    recordedAt: timestampSchema,
    truthBoundary: z.literal("SUPPLIED_ASSET_USE_NOT_AUTHENTICATED"),
})
    .strict()
    .superRefine((value, context) => {
    if (value.purpose === "DIRECTION_KIT_COMPONENT" &&
        (!value.directionId || !value.kitId || value.bookId || value.revisionId))
        context.addIssue({
            code: "custom",
            path: ["purpose"],
            message: "Direction-kit use requires a kit and direction and cannot bind customer-book identity.",
        });
    if (value.purpose === "CUSTOMER_BOOK_RENDITION" &&
        (!value.bookId || !value.revisionId || !value.directionId))
        context.addIssue({
            code: "custom",
            path: ["bookId"],
            message: "Customer-book use requires exact book, revision, and direction identity.",
        });
    if (value.meaning === "MEANINGFUL" &&
        (!value.contextualAltDigestSha256 ||
            !value.contextualAltSourceClass ||
            !value.contextualAltSourceBindingDigestSha256))
        context.addIssue({
            code: "custom",
            path: ["contextualAltDigestSha256"],
            message: "Meaningful asset use requires a context-specific alternate-description digest and exact authored/approved/semantic source binding.",
        });
    if (value.meaning !== "MEANINGFUL" &&
        (value.contextualAltDigestSha256 ||
            value.contextualAltSourceClass ||
            value.contextualAltSourceBindingDigestSha256 ||
            value.contextualAltBookBinding))
        context.addIssue({
            code: "custom",
            path: ["contextualAltDigestSha256"],
            message: "Only meaningful media carries a contextual alternate-description digest.",
        });
    if (value.purpose === "DIRECTION_KIT_COMPONENT" && value.meaning === "MEANINGFUL")
        context.addIssue({
            code: "custom",
            path: ["meaning"],
            message: "Reusable kits cannot pre-author meaningful book-context alt text; bind a later semantic component or book rendition use.",
        });
    if (value.purpose === "CUSTOMER_BOOK_RENDITION" &&
        value.meaning === "MEANINGFUL" &&
        (!value.contextualAltBookBinding ||
            value.contextualAltBookBinding.bookId !== value.bookId ||
            value.contextualAltBookBinding.revisionId !== value.revisionId))
        context.addIssue({
            code: "custom",
            path: ["contextualAltBookBinding"],
            message: "Customer-rendition alt text must bind the exact book revision it describes.",
        });
    if (value.meaning === "COMPONENT_APPEARANCE_TARGET" &&
        value.renderMode !== "SEMANTIC_COMPONENT_REFERENCE")
        context.addIssue({
            code: "custom",
            path: ["renderMode"],
            message: "A component appearance target must be rebuilt as a semantic component.",
        });
});
export const bookPublishingAssetUseV1Schema = bookPublishingAssetUseBodyV1Schema.safeExtend({
    useDigestSha256: sha256Schema,
});
export const bookPublishingAssetUseExactBindingV1Schema = z
    .object({
    useId: opaqueIdSchema,
    useDigestSha256: sha256Schema,
})
    .strict();
//# sourceMappingURL=book-publishing-asset.js.map